import { createSelector } from "@reduxjs/toolkit"
import { FormRootState } from "./formStore"
import { selectExport } from "./selectForm"
import _ from "lodash"
import { cidForDirItemId, CommonResponse, exportMemberAge, statusValue, substatusValue } from "@fusion-advantage/formlib"
import { selectConceptStatus } from "./selectConceptStatus2"


export interface MahcAssessmentDetailsItem {
	label: string,
	value: number,
	meetsCriteria: boolean,
}

export interface MahcAssessmentDetails {
	age: MahcAssessmentDetailsItem,
	diagnoses: MahcAssessmentDetailsItem,
	priorFalls: MahcAssessmentDetailsItem,
	incontinence: MahcAssessmentDetailsItem,
	visualImpairment: MahcAssessmentDetailsItem,
	impairedFunctionalMobility: MahcAssessmentDetailsItem,
	environmentHazards: MahcAssessmentDetailsItem,
	polyPharmacy: MahcAssessmentDetailsItem,
	painAffectingFunction: MahcAssessmentDetailsItem,
	cognitiveImpairment: MahcAssessmentDetailsItem,
}

const selectMahcAssessmentPainHasImpact = createSelector(
	(state: FormRootState) => substatusValue(selectConceptStatus(state, cidForDirItemId(679)), "c48d23256ae3e8f86597a6ee067ce7bc9"),
	(state: FormRootState) => substatusValue(selectConceptStatus(state, cidForDirItemId(679)), "c35987af106d736a2fc85bd102afae26a"),
	(state: FormRootState) => substatusValue(selectConceptStatus(state, cidForDirItemId(679)), "c7faddd91c93dfcc321c98a94636127e9"),
	(state: FormRootState) => substatusValue(selectConceptStatus(state, cidForDirItemId(679)), "cbadafa94647075a0cb046aebfeced0de"),
	(state: FormRootState) => substatusValue(selectConceptStatus(state, cidForDirItemId(679)), "c16ec0319aa01cdd035b0df7d528df851"),
	(state: FormRootState) => substatusValue(selectConceptStatus(state, cidForDirItemId(679)), "cdcaf1f6f59a09ee2ab142322b68879d2"),
	(noImpact, smallImpact, moderateImpact, extremeImpact, refused, unknown) => {
		if(noImpact || refused || unknown) {
			return false
		}

		if(smallImpact || moderateImpact || extremeImpact) {
			return true
		}

		return false
	},
)

export const selectMahcAssessmentDetails = createSelector(
	(state: FormRootState) => selectExport(state),
	(state: FormRootState) => state.diagnosisReview.models.length,
	(state: FormRootState) => state.drug.models.length,
	(state: FormRootState) => substatusValue(selectConceptStatus(state, cidForDirItemId(1299)), CommonResponse.Positive),
	(state: FormRootState) => statusValue(selectConceptStatus(state, cidForDirItemId(595))),
	(state: FormRootState) => substatusValue(selectConceptStatus(state, cidForDirItemId(1300)), CommonResponse.Positive),
	(state: FormRootState) => substatusValue(selectConceptStatus(state, cidForDirItemId(689)), CommonResponse.Yes),
	(state: FormRootState) => substatusValue(selectConceptStatus(state, cidForDirItemId(693)), CommonResponse.Yes),
	(state: FormRootState) => selectMahcAssessmentPainHasImpact(state),
	(state: FormRootState) => statusValue(selectConceptStatus(state, cidForDirItemId(687))),
	(exp, diagnosesCount, drugCount, priorFallsStatus, incontinenceStatus, visualImpairmentStatus, impairedFunctionalMobilityStatus, environmentHazardsStatus, painHasImpact, cognitiveImpairmentStatus): MahcAssessmentDetails => {
		const age = (exportMemberAge(exp) ?? 0) >= 65

		return {
			age: {
				label: "Age (65+)",
				value: 1,
				meetsCriteria: age,
			},
			diagnoses: {
				label: "Diagnosis count (3+)",
				value: 1,
				meetsCriteria: diagnosesCount >= 3,
			},
			priorFalls: {
				label: "Prior falls within 3 months",
				value: 1,
				meetsCriteria: !!priorFallsStatus,
			},
			incontinence: {
				label: "Incontinence",
				value: 1,
				meetsCriteria: !!incontinenceStatus,
			},
			visualImpairment: {
				label: "Visual impairment",
				value: 1,
				meetsCriteria: !!visualImpairmentStatus,
			},
			impairedFunctionalMobility: {
				label: "Impaired functional mobility",
				value: 1,
				meetsCriteria: !!impairedFunctionalMobilityStatus,
			},
			environmentHazards: {
				label: "Environmental hazards",
				value: 1,
				meetsCriteria: !!environmentHazardsStatus,
			},
			polyPharmacy: {
				label: "Poly-pharmacy (4+ prescriptions)",
				value: 1,
				meetsCriteria: drugCount >= 4,
			},
			painAffectingFunction: {
				label: "Pain affecting function",
				value: 1,
				meetsCriteria: painHasImpact,
			},
			cognitiveImpairment: {
				label: "Cognitive impairment",
				value: 1,
				meetsCriteria: !!cognitiveImpairmentStatus,
			},
		}
	},
)